// src/api/axiosInstance.js
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { getApiBase } from "./apiUtils";

/* ────────────────────────────────────────────────────────────────────────────
 * Base
 * ──────────────────────────────────────────────────────────────────────────── */
const API_BASE = getApiBase(); // e.g. "https://.../api" หรือ "/api"

if (process.env.NODE_ENV !== "production") {
  // eslint-disable-next-line no-console
  console.log("🌐 axiosInstance baseURL =", API_BASE);
}

/* ────────────────────────────────────────────────────────────────────────────
 * Public / Protected
 * ──────────────────────────────────────────────────────────────────────────── */
const PUBLIC_PREFIXES = [
  "/auth",
  "/gallery",
  "/multi-step-search",
  "/car-options",
  "/insurance-options",
  "/bigquery",
  "/ping",
  "/health",
  "/uploads",
];
const PROTECTED_PATHS = ["/bigquery/repair/defaults/upsert"]; // admin-only
const PROTECT_DEFAULTS =
  (process.env.REACT_APP_PROTECT_DEFAULTS || "true").toLowerCase() === "true";

/* ────────────────────────────────────────────────────────────────────────────
 * Token storage
 * ──────────────────────────────────────────────────────────────────────────── */
const AUTH_TOKEN_KEY = "authToken";
const REFRESH_TOKEN_KEY = "refreshToken";
const LOGIN_PATH = "/login";

const safeStorage = {
  get: (k) => { try { return localStorage.getItem(k); } catch { return null; } },
  set: (k, v) => { try { localStorage.setItem(k, v); } catch {} },
  remove: (k) => { try { localStorage.removeItem(k); } catch {} },
};

const getAuthToken = () => safeStorage.get(AUTH_TOKEN_KEY);
const getRefreshToken = () => safeStorage.get(REFRESH_TOKEN_KEY);
const saveAuthToken = (t) => { if (t) safeStorage.set(AUTH_TOKEN_KEY, t); };
const saveRefreshToken = (t) => { if (t) safeStorage.set(REFRESH_TOKEN_KEY, t); };

export const saveTokens = (payload = {}) => {
  const at = payload.access || payload.access_token;
  const rt = payload.refresh || payload.refresh_token;
  if (at) saveAuthToken(at);
  if (rt) saveRefreshToken(rt);
};

const clearTokens = (opts = {}) => {
  const { redirect = true, next } = opts;
  safeStorage.remove(AUTH_TOKEN_KEY);
  safeStorage.remove(REFRESH_TOKEN_KEY);
  safeStorage.remove("userRole");
  safeStorage.remove("adminAuthToken");
  safeStorage.remove("adminRefreshToken");
  if (redirect && typeof window !== "undefined" && window.location) {
    console.warn("🛑 Token invalid. Redirecting to /login ..."); // eslint-disable-line no-console
    const here = next || (window.location.pathname + window.location.search);
    const onLogin = window.location.pathname.replace(/\/+$/, "") === LOGIN_PATH;
    const target = onLogin ? LOGIN_PATH : `${LOGIN_PATH}?next=${encodeURIComponent(here)}`;
    window.location.replace(target);
  }
};

const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const decoded = jwtDecode(token);
    return decoded.exp < Date.now() / 1000;
  } catch {
    return true;
  }
};

/* ────────────────────────────────────────────────────────────────────────────
 * Utils
 * ──────────────────────────────────────────────────────────────────────────── */
const isFormData = (v) => typeof FormData !== "undefined" && v instanceof FormData;

const normalizePath = (p) => {
  if (!p) return "/";
  try {
    // ให้ new URL ช่วย normalize pathname (รองรับ absolute/relative)
    const u = new URL(p, API_BASE);
    return u.pathname;
  } catch {
    return p.startsWith("/") ? p : `/${p}`;
  }
};

const isProtectedPath = (p) =>
  PROTECT_DEFAULTS && PROTECTED_PATHS.some((x) => normalizePath(p).startsWith(x));

const isPublicPath = (p) => {
  const path = normalizePath(p);
  if (isProtectedPath(path)) return false;
  return PUBLIC_PREFIXES.some((prefix) => path.startsWith(prefix));
};

const extractErrorMessage = (error) => {
  const d = error?.response?.data;
  return d?.error || d?.message || error?.message || "API request failed";
};

/* ────────────────────────────────────────────────────────────────────────────
 * Axios instance
 * ──────────────────────────────────────────────────────────────────────────── */
const axiosInstance = axios.create({
  baseURL: API_BASE,
  timeout: 45000,
  withCredentials: false,
});

/* ────────────────────────────────────────────────────────────────────────────
 * Soft-Guard: เตือนเคส /fetch_packages ที่คีย์หลักอาจขาด แต่ "ไม่บล็อก"
 * ──────────────────────────────────────────────────────────────────────────── */
const markIfFetchPackagesMissingKeys = (config) => {
  try {
    const url = String(config?.url || "");
    const isPost = String(config?.method || "get").toLowerCase() === "post";
    if (!isPost || !/multi-step-search\/fetch_packages/.test(url)) return;

    const raw = config?.data;
    const data = typeof raw === "string" ? JSON.parse(raw || "{}") : (raw || {});
    // ฝั่ง BE รองรับชื่อ year → เดิม guard ใช้ car_model_year ทำให้บล็อกเอง
    const { car_brand, car_model, year } = data || {};
    if (!car_brand || !car_model || !year) {
      // เตือนเฉย ๆ เพื่อกันหายทั้งลิสต์ แต่ "ไม่ throw"
      // eslint-disable-next-line no-console
      console.warn("⚠️ fetch_packages without full keys:", { car_brand, car_model, year });
      config.headers = config.headers || {};
      config.headers["X-Client-Guard"] = "warn-missing-keys";
    }
  } catch {
    /* no-op */
  }
};

/* ────────────────────────────────────────────────────────────────────────────
 * Interceptor: Request
 * ──────────────────────────────────────────────────────────────────────────── */
axiosInstance.interceptors.request.use(
  async (config) => {
    // ไม่ยุ่งกับ config.url ให้ axios + baseURL จัดการ (กัน strip 'api/' เกินเหตุ)
    // จัด Content-Type
    if (isFormData(config.data)) {
      config.headers = config.headers || {};
      delete config.headers["Content-Type"];
      delete config.headers["content-type"];
      if (axiosInstance.defaults?.headers?.post) {
        delete axiosInstance.defaults.headers.post["Content-Type"];
        delete axiosInstance.defaults.headers.post["content-type"];
      }
    } else {
      config.headers = config.headers || {};
      if (!config.headers["Content-Type"] && !config.headers["content-type"]) {
        config.headers["Content-Type"] = "application/json";
      }
    }

    // แนบ Authorization เฉพาะ non-public
    if (!isPublicPath(config.url)) {
      const token = getAuthToken();
      if (token && !isTokenExpired(token)) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    // เตือน (ไม่บล็อก) เคส fetch_packages คีย์ไม่ครบ
    markIfFetchPackagesMissingKeys(config);

    return config;
  },
  (error) => Promise.reject(error)
);

/* ────────────────────────────────────────────────────────────────────────────
 * Refresh token (raw axios)
 * ──────────────────────────────────────────────────────────────────────────── */
const refreshAccessToken = async () => {
  const rt = getRefreshToken();
  if (!rt) return null;
  try {
    const { data } = await axios.post(
      `${API_BASE.replace(/\/+$/, "")}/auth/refresh`,
      { refresh_token: rt },
      { withCredentials: false, headers: { "Content-Type": "application/json" } }
    );
    const newToken = data?.access_token;
    if (!newToken) return null;
    saveAuthToken(newToken);
    return newToken;
  } catch (err) {
    console.error("❌ refreshAccessToken failed:", extractErrorMessage(err)); // eslint-disable-line no-console
    return null;
  }
};

/* ────────────────────────────────────────────────────────────────────────────
 * Interceptor: Response + Auto-refresh 401
 * ──────────────────────────────────────────────────────────────────────────── */
let isRefreshing = false;
let refreshSubscribers = [];
const subscribeTokenRefresh = (cb) => refreshSubscribers.push(cb);
const onRefreshed = (token) => { refreshSubscribers.forEach((cb) => cb(token)); refreshSubscribers = []; };

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config || {};
    const status = error?.response?.status;
    const path = normalizePath(originalRequest?.url || "");

    // public path → ไม่ต้อง refresh
    if (status === 401 && isPublicPath(path)) {
      return Promise.reject(error);
    }

    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve) => {
          subscribeTokenRefresh((token) => {
            if (token) {
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            resolve(axiosInstance(originalRequest));
          });
        });
      }

      isRefreshing = true;
      const newToken = await refreshAccessToken();
      isRefreshing = false;

      if (!newToken) {
        clearTokens();
        return Promise.reject(error);
      }

      onRefreshed(newToken);
      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return axiosInstance(originalRequest);
    }

    const msg = extractErrorMessage(error);
    console.error("❌ API Error:", {
      url: originalRequest?.url,
      status,
      data: error?.response?.data,
      msg,
    }); // eslint-disable-line no-console
    error.normalizedMessage = msg;

    return Promise.reject(error);
  }
);

export default axiosInstance;
